/**
 * Persona Photo Library
 *
 * 20 default portrait photos (10 female, 10 male) used to mimic personas.
 * Sourced from randomuser.me — stable free stock portraits.
 *
 * Users can:
 *  - pick from this internal library
 *  - upload a custom photo (see PersonaAvatarUploader)
 *
 * Gender is heuristically detected from the first name to pre-filter the
 * library. Detection is best-effort — covers common Western + a handful of
 * non-Western given names. Unknown names fall back to the full library.
 */

export type Gender = 'female' | 'male' | 'unknown'

export interface LibraryPhoto {
  id: string
  url: string
  gender: Exclude<Gender, 'unknown'>
}

export const DEFAULT_PHOTOS: LibraryPhoto[] = [
  // Female portraits
  { id: 'f1', gender: 'female', url: 'https://randomuser.me/api/portraits/women/4.jpg' },
  { id: 'f2', gender: 'female', url: 'https://randomuser.me/api/portraits/women/12.jpg' },
  { id: 'f3', gender: 'female', url: 'https://randomuser.me/api/portraits/women/19.jpg' },
  { id: 'f4', gender: 'female', url: 'https://randomuser.me/api/portraits/women/26.jpg' },
  { id: 'f5', gender: 'female', url: 'https://randomuser.me/api/portraits/women/35.jpg' },
  { id: 'f6', gender: 'female', url: 'https://randomuser.me/api/portraits/women/44.jpg' },
  { id: 'f7', gender: 'female', url: 'https://randomuser.me/api/portraits/women/56.jpg' },
  { id: 'f8', gender: 'female', url: 'https://randomuser.me/api/portraits/women/65.jpg' },
  { id: 'f9', gender: 'female', url: 'https://randomuser.me/api/portraits/women/77.jpg' },
  { id: 'f10', gender: 'female', url: 'https://randomuser.me/api/portraits/women/89.jpg' },
  // Male portraits
  { id: 'm1', gender: 'male', url: 'https://randomuser.me/api/portraits/men/3.jpg' },
  { id: 'm2', gender: 'male', url: 'https://randomuser.me/api/portraits/men/11.jpg' },
  { id: 'm3', gender: 'male', url: 'https://randomuser.me/api/portraits/men/18.jpg' },
  { id: 'm4', gender: 'male', url: 'https://randomuser.me/api/portraits/men/22.jpg' },
  { id: 'm5', gender: 'male', url: 'https://randomuser.me/api/portraits/men/32.jpg' },
  { id: 'm6', gender: 'male', url: 'https://randomuser.me/api/portraits/men/41.jpg' },
  { id: 'm7', gender: 'male', url: 'https://randomuser.me/api/portraits/men/55.jpg' },
  { id: 'm8', gender: 'male', url: 'https://randomuser.me/api/portraits/men/67.jpg' },
  { id: 'm9', gender: 'male', url: 'https://randomuser.me/api/portraits/men/75.jpg' },
  { id: 'm10', gender: 'male', url: 'https://randomuser.me/api/portraits/men/88.jpg' },
]

// Curated common given names. Not exhaustive — covers common English, Spanish,
// French, German, Italian, Nordic, and a handful of Asian/Arabic names so
// realistic persona names tend to resolve correctly.
const FEMALE_NAMES = new Set<string>([
  'mary', 'patricia', 'jennifer', 'linda', 'elizabeth', 'barbara', 'susan', 'jessica', 'sarah', 'karen',
  'nancy', 'lisa', 'betty', 'helen', 'sandra', 'donna', 'carol', 'ruth', 'sharon', 'michelle',
  'laura', 'emily', 'kimberly', 'deborah', 'amy', 'angela', 'ashley', 'brenda', 'emma', 'olivia',
  'cynthia', 'marie', 'janet', 'catherine', 'frances', 'christine', 'samantha', 'debra', 'rachel', 'carolyn',
  'virginia', 'maria', 'heather', 'diane', 'julie', 'joyce', 'victoria', 'kelly', 'christina', 'joan',
  'evelyn', 'lauren', 'judith', 'megan', 'cheryl', 'andrea', 'hannah', 'jacqueline', 'martha', 'gloria',
  'teresa', 'ann', 'sara', 'madison', 'grace', 'rose', 'sophia', 'natalie', 'alice', 'julia', 'kathleen',
  'anna', 'eva', 'clara', 'lea', 'lena', 'mia', 'sofia', 'chloe', 'camille', 'amelie', 'claire', 'ines',
  'delphine', 'sophie', 'marion', 'celine', 'juliette', 'manon', 'lucie', 'margot', 'alba', 'carmen',
  'lucia', 'isabel', 'valentina', 'beatriz', 'pilar', 'elena', 'martina', 'paola', 'chiara', 'giulia',
  'greta', 'astrid', 'ingrid', 'sigrid', 'freja', 'linnea', 'yuki', 'sakura', 'mei', 'lin', 'aisha', 'fatima',
  'zara', 'amina', 'layla', 'nadia', 'priya', 'aditi', 'riya', 'anjali',
])

const MALE_NAMES = new Set<string>([
  'james', 'john', 'robert', 'michael', 'william', 'david', 'richard', 'joseph', 'thomas', 'charles',
  'christopher', 'daniel', 'matthew', 'anthony', 'donald', 'mark', 'paul', 'steven', 'andrew', 'kenneth',
  'george', 'joshua', 'kevin', 'brian', 'edward', 'ronald', 'timothy', 'jason', 'jeffrey', 'ryan',
  'gary', 'nicholas', 'eric', 'jonathan', 'stephen', 'larry', 'justin', 'scott', 'brandon', 'benjamin',
  'samuel', 'gregory', 'frank', 'alexander', 'raymond', 'patrick', 'jack', 'dennis', 'jerry', 'tyler',
  'aaron', 'jose', 'henry', 'adam', 'douglas', 'nathan', 'peter', 'zachary', 'kyle', 'walter', 'ethan',
  'jeremy', 'harold', 'keith', 'roger', 'gerald', 'carl', 'lawrence', 'noah', 'oliver', 'lucas', 'liam',
  'mason', 'logan', 'elijah', 'caleb', 'isaac', 'owen', 'max', 'leo', 'felix', 'hugo', 'jules', 'louis',
  'gabriel', 'arthur', 'thomas', 'raphael', 'martin', 'baptiste', 'nicolas', 'antoine', 'pierre',
  'olivier', 'pascal', 'florent', 'julien', 'sebastien', 'vincent', 'cedric', 'carlos', 'juan', 'diego',
  'luis', 'miguel', 'pedro', 'javier', 'pablo', 'andres', 'marco', 'luca', 'matteo', 'lorenzo',
  'alessandro', 'giovanni', 'franco', 'enzo', 'hans', 'klaus', 'otto', 'stefan', 'erik', 'lars', 'nils',
  'hiro', 'ken', 'takeshi', 'ahmed', 'omar', 'yusuf', 'ali', 'hassan', 'raj', 'arjun', 'vikram', 'rohan',
  'dimitri', 'dimitry',
])

export function detectGender(name: string | undefined | null): Gender {
  if (!name) return 'unknown'
  const first = name.trim().split(/\s+/)[0]?.toLowerCase()
  if (!first) return 'unknown'
  // Strip trailing punctuation/accents for matching
  const normalized = first.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z]/g, '')
  if (FEMALE_NAMES.has(normalized)) return 'female'
  if (MALE_NAMES.has(normalized)) return 'male'
  // Heuristic: many female names end in -a, -ia, -ie, -ine (best-effort fallback)
  if (/(a|ia|ie|ine|elle|ette|een|ina)$/.test(normalized)) return 'female'
  return 'unknown'
}

export function photosForGender(gender: Gender): LibraryPhoto[] {
  if (gender === 'unknown') return DEFAULT_PHOTOS
  return DEFAULT_PHOTOS.filter(p => p.gender === gender)
}

/** Simple deterministic string hash so a given name maps to the same photo. */
function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}

/**
 * Pick a stable default photo for a given name. Same name → same photo across
 * sessions. Returns undefined when name is empty (so callers can show
 * initials placeholder instead).
 */
export function pickDefaultPhoto(name: string | undefined | null): LibraryPhoto | undefined {
  if (!name || !name.trim()) return undefined
  const pool = photosForGender(detectGender(name))
  if (pool.length === 0) return undefined
  return pool[hashString(name.trim().toLowerCase()) % pool.length]
}
